import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getLibraries,
  getLibraryStats,
  createLibrary,
  updateLibrary,
  deleteLibrary,
  searchGroups,
  getCategories,
  createCategory,
  deleteCategory,
  bulkUpdateGroupCategories,
  getCoverageStats,
  getCoverageByIndustry,
  getIndustries,
  excludeGroup,
  includeGroup,
  searchAccounts,
  bulkAssignAccount,
  bulkAssignOccupation
} from './core/_requests'
import moment from 'moment'
import 'moment/locale/he'
moment.locale('he')

// Occupation verticals (mirrors Account.library_occupation)
const OCCUPATION_OPTIONS: { value: string; label: string }[] = [
  { value: 'real_estate', label: 'נדל"ן' },
  { value: 'cars', label: 'רכב' },
  { value: 'hr', label: 'כח אדם' },
  { value: 'other', label: 'אחר' },
]
const occupationLabel = (occupation?: string | null, other?: string | null): string | null => {
  if (!occupation) return null
  if (occupation === 'other') return other || 'אחר'
  return OCCUPATION_OPTIONS.find(o => o.value === occupation)?.label || occupation
}

interface Library {
  _id: string
  name: string
  is_public: boolean
  group_count: number
  categories?: { _id: string; name: string }[]
  account?: { first_name?: string; last_name?: string; email?: string }
  occupation?: string | null
  occupation_other?: string | null
  created_at: string
  modified_at?: string
  updated_at?: string
}

interface Category {
  _id: string
  name: string
  group_count: number
}

interface Group {
  _id: string
  name: string
  group_url: string
  description?: string
  location?: string
  follow_count?: number
  category?: { _id: string; name: string }[]
}

interface Stats {
  totalLibraries: number
  publicLibraries: number
  totalCategories: number
  totalGroups: number
}

interface CoverageStats {
  campaigns: {
    total: number
    categorized: number
    finalized: number
    withLibrary: number
    categorizationPercent: number
    libraryAssignmentPercent: number
  }
  libraries: {
    total: number
    public: number
    finished: number
  }
  accounts: {
    active: number
  }
}

interface Industry {
  _id: string
  name: string
  name_he: string
}

const LibrariesPage: React.FC = () => {
  const navigate = useNavigate()

  // Libraries state
  const [stats, setStats] = useState<Stats | null>(null)
  const [libraries, setLibraries] = useState<Library[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [localSearch, setLocalSearch] = useState('')

  // Categories state
  const [categories, setCategories] = useState<Category[]>([])
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')

  // Group search state
  const [showGroupSearch, setShowGroupSearch] = useState(false)
  const [groupKeywords, setGroupKeywords] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [minFollowers, setMinFollowers] = useState('')
  const [searchedGroups, setSearchedGroups] = useState<Group[]>([])
  const [searchingGroups, setSearchingGroups] = useState(false)
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set())
  const [groupSearchPage, setGroupSearchPage] = useState(1)
  const [groupSearchTotalPages, setGroupSearchTotalPages] = useState(1)

  // Create library modal
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newLibraryName, setNewLibraryName] = useState('')
  const [newLibraryPublic, setNewLibraryPublic] = useState(true)
  const [newLibraryCategories, setNewLibraryCategories] = useState<string[]>([])
  const [newLibraryAccountId, setNewLibraryAccountId] = useState('')
  const [newLibraryAccountName, setNewLibraryAccountName] = useState('')
  const [accountSearchQuery, setAccountSearchQuery] = useState('')
  const [accountSearchResults, setAccountSearchResults] = useState<any[]>([])
  const [searchingAccounts, setSearchingAccounts] = useState(false)
  const [creating, setCreating] = useState(false)

  // Bulk category modal
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false)
  const [bulkCategoryIds, setBulkCategoryIds] = useState<string[]>([])
  const [bulkMode, setBulkMode] = useState<'add' | 'remove' | 'set'>('add')

  // Coverage stats
  const [coverageStats, setCoverageStats] = useState<CoverageStats | null>(null)
  const [industries, setIndustries] = useState<Industry[]>([])

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [industryFilter, setIndustryFilter] = useState<string>('')
  // Filter by last-modified (updated_at) date range — YYYY-MM-DD strings from <input type=date>.
  const [modifiedFrom, setModifiedFrom] = useState<string>('')
  const [modifiedTo, setModifiedTo] = useState<string>('')

  // Library selection for bulk actions
  const [selectedLibraries, setSelectedLibraries] = useState<Set<string>>(new Set())
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const [showBulkAssignAccountModal, setShowBulkAssignAccountModal] = useState(false)
  const [bulkAssignAccountId, setBulkAssignAccountId] = useState('')
  const [bulkAssignAccountName, setBulkAssignAccountName] = useState('')
  const [bulkAssignAccountSearch, setBulkAssignAccountSearch] = useState('')
  const [bulkAssignAccountResults, setBulkAssignAccountResults] = useState<any[]>([])
  const [bulkAssignSearching, setBulkAssignSearching] = useState(false)
  const [showBulkAssignOccupationModal, setShowBulkAssignOccupationModal] = useState(false)
  const [bulkOccupation, setBulkOccupation] = useState('')
  const [bulkOccupationOther, setBulkOccupationOther] = useState('')

  // UI state
  const [hoveredCard, setHoveredCard] = useState<number | null>(null)
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'dashboard' | 'public-libraries' | 'private-libraries' | 'groups' | 'categories'>('dashboard')

  useEffect(() => {
    if (activeTab === 'public-libraries' || activeTab === 'private-libraries') {
      loadData()
    } else if (activeTab === 'dashboard') {
      loadDashboard()
    }
    loadCategories()
    loadIndustries()
    // Clear library selection when switching tabs
    setSelectedLibraries(new Set())
  }, [searchQuery, activeTab, currentPage, statusFilter, industryFilter, modifiedFrom, modifiedTo])

  const loadData = async () => {
    setLoading(true)
    try {
      const params: any = { page: currentPage, limit: 400 }
      if (searchQuery) params.search = searchQuery
      params.is_public = activeTab === 'public-libraries' ? 'true' : 'false'
      if (statusFilter) params.status = statusFilter
      if (industryFilter) params.industry = industryFilter
      // Send full ISO timestamps for the selected days in the BROWSER's timezone so
      // the window lines up with the localized "עודכן" column the admin sees.
      if (modifiedFrom) params.modified_from = new Date(`${modifiedFrom}T00:00:00`).toISOString()
      if (modifiedTo) params.modified_to = new Date(`${modifiedTo}T23:59:59.999`).toISOString()
      // When filtering by modified date, order by it too so the column is sorted and
      // the range is visibly correct (default ordering is by created date).
      if (modifiedFrom || modifiedTo) params.sort = '-modified_at'

      const [statsRes, librariesRes] = await Promise.all([
        getLibraryStats(),
        getLibraries(params)
      ])

      setStats(statsRes)
      setLibraries(librariesRes.libraries)
      setTotalPages(librariesRes.pagination.pages)
    } catch (error) {
      console.error('Failed to load libraries:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadDashboard = async () => {
    setLoading(true)
    try {
      const [statsRes, coverageRes] = await Promise.all([
        getLibraryStats(),
        getCoverageStats()
      ])
      setStats(statsRes)
      setCoverageStats(coverageRes)
    } catch (error) {
      console.error('Failed to load dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadIndustries = async () => {
    try {
      const res = await getIndustries()
      setIndustries(res.industries || [])
    } catch (error) {
      console.error('Failed to load industries:', error)
    }
  }

  const loadCategories = async () => {
    try {
      const res = await getCategories()
      setCategories(res.categories)
    } catch (error) {
      console.error('Failed to load categories:', error)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchQuery(localSearch)
    setCurrentPage(1)
  }

  const handleCreateLibrary = async () => {
    if (!newLibraryName.trim()) return
    setCreating(true)
    try {
      await createLibrary({
        name: newLibraryName.trim(),
        is_public: newLibraryPublic,
        categories: newLibraryCategories,
        ...(newLibraryAccountId ? { account_id: newLibraryAccountId } : {})
      })
      setShowCreateModal(false)
      setNewLibraryName('')
      setNewLibraryPublic(true)
      setNewLibraryCategories([])
      setNewLibraryAccountId('')
      setNewLibraryAccountName('')
      setAccountSearchQuery('')
      setAccountSearchResults([])
      loadData()
    } catch (error: any) {
      alert(error.message || 'Failed to create library')
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteLibrary = async (id: string, name: string) => {
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק את הספרייה "${name}"?`)) return
    try {
      await deleteLibrary(id)
      loadData()
    } catch (error) {
      alert('Failed to delete library')
    }
  }

  const handleMakePublic = async (id: string, name: string) => {
    if (!window.confirm(`האם אתה בטוח שברצונך להעביר את הספרייה "${name}" לספריות ציבוריות?`)) return
    try {
      await updateLibrary(id, { is_public: true })
      loadData()
    } catch (error) {
      alert('Failed to make library public')
    }
  }

  const handleMakePrivate = async (id: string, name: string) => {
    if (!window.confirm(`האם אתה בטוח שברצונך להעביר את הספרייה "${name}" לספריות פרטיות?`)) return
    try {
      await updateLibrary(id, { is_public: false })
      loadData()
    } catch (error) {
      alert('Failed to make library private')
    }
  }

  const handleSearchGroups = async (page = 1) => {
    if (!groupKeywords.trim() && !selectedCategory) return
    setSearchingGroups(true)
    setGroupSearchPage(page)
    try {
      const res = await searchGroups({
        keywords: groupKeywords,
        category: selectedCategory,
        minFollowers: minFollowers ? parseInt(minFollowers) : undefined,
        page,
        limit: 50
      })
      setSearchedGroups(res.groups)
      setGroupSearchTotalPages(res.pagination.pages)
    } catch (error) {
      console.error('Failed to search groups:', error)
    } finally {
      setSearchingGroups(false)
    }
  }

  const toggleGroupSelection = (groupId: string) => {
    setSelectedGroups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupId)) {
        newSet.delete(groupId)
      } else {
        newSet.add(groupId)
      }
      return newSet
    })
  }

  const selectAllGroups = () => {
    setSelectedGroups(new Set(searchedGroups.map(g => g._id)))
  }

  const clearSelection = () => {
    setSelectedGroups(new Set())
  }

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return
    try {
      await createCategory(newCategoryName.trim())
      setNewCategoryName('')
      setShowCategoryModal(false)
      loadCategories()
      loadData()
    } catch (error: any) {
      alert(error.message || 'Failed to create category')
    }
  }

  const handleDeleteCategory = async (id: string, name: string) => {
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק את הקטגוריה "${name}"?`)) return
    try {
      await deleteCategory(id)
      loadCategories()
      loadData()
    } catch (error) {
      alert('Failed to delete category')
    }
  }

  const handleBulkCategoryApply = async () => {
    if (selectedGroups.size === 0 || bulkCategoryIds.length === 0) return
    try {
      await bulkUpdateGroupCategories(Array.from(selectedGroups), bulkCategoryIds, bulkMode)
      setShowBulkCategoryModal(false)
      setBulkCategoryIds([])
      handleSearchGroups(groupSearchPage)
    } catch (error) {
      alert('Failed to apply categories')
    }
  }

  // Library selection handlers
  const toggleLibrarySelection = (libraryId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedLibraries(prev => {
      const newSet = new Set(prev)
      if (newSet.has(libraryId)) {
        newSet.delete(libraryId)
      } else {
        newSet.add(libraryId)
      }
      return newSet
    })
  }

  const selectAllLibraries = () => {
    if (selectedLibraries.size === libraries.length) {
      setSelectedLibraries(new Set())
    } else {
      setSelectedLibraries(new Set(libraries.map(lib => lib._id)))
    }
  }

  const handleBulkMakePublic = async () => {
    if (selectedLibraries.size === 0) return
    if (!window.confirm(`האם אתה בטוח שברצונך להעביר ${selectedLibraries.size} ספריות לציבוריות?`)) return
    setBulkUpdating(true)
    try {
      await Promise.all(Array.from(selectedLibraries).map(id => updateLibrary(id, { is_public: true })))
      setSelectedLibraries(new Set())
      loadData()
    } catch (error) {
      alert('Failed to update libraries')
    } finally {
      setBulkUpdating(false)
    }
  }

  const handleBulkMakePrivate = async () => {
    if (selectedLibraries.size === 0) return
    if (!window.confirm(`האם אתה בטוח שברצונך להעביר ${selectedLibraries.size} ספריות לפרטיות?`)) return
    setBulkUpdating(true)
    try {
      await Promise.all(Array.from(selectedLibraries).map(id => updateLibrary(id, { is_public: false })))
      setSelectedLibraries(new Set())
      loadData()
    } catch (error) {
      alert('Failed to update libraries')
    } finally {
      setBulkUpdating(false)
    }
  }

  const handleBulkAssignAccount = async () => {
    if (selectedLibraries.size === 0 || !bulkAssignAccountId) return
    setBulkUpdating(true)
    try {
      await bulkAssignAccount(Array.from(selectedLibraries), bulkAssignAccountId)
      setSelectedLibraries(new Set())
      setShowBulkAssignAccountModal(false)
      setBulkAssignAccountId('')
      setBulkAssignAccountName('')
      setBulkAssignAccountSearch('')
      setBulkAssignAccountResults([])
      loadData()
    } catch (error) {
      alert('Failed to assign account')
    } finally {
      setBulkUpdating(false)
    }
  }

  const handleBulkAssignOccupation = async () => {
    if (selectedLibraries.size === 0 || !bulkOccupation) return
    if (bulkOccupation === 'other' && !bulkOccupationOther.trim()) {
      alert('יש להזין תיאור עיסוק')
      return
    }
    setBulkUpdating(true)
    try {
      await bulkAssignOccupation(
        Array.from(selectedLibraries),
        bulkOccupation,
        bulkOccupation === 'other' ? bulkOccupationOther.trim() : undefined
      )
      setSelectedLibraries(new Set())
      setShowBulkAssignOccupationModal(false)
      setBulkOccupation('')
      setBulkOccupationOther('')
      loadData()
    } catch (error) {
      alert('Failed to assign occupation')
    } finally {
      setBulkUpdating(false)
    }
  }

  const statsData = [
    {
      label: 'סה"כ ספריות',
      value: stats?.totalLibraries || 0,
      icon: 'bi-collection-fill',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      label: 'ספריות ציבוריות',
      value: stats?.publicLibraries || 0,
      icon: 'bi-globe',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
    {
      label: 'קטגוריות',
      value: stats?.totalCategories || 0,
      icon: 'bi-tags-fill',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
      label: 'סה"כ קבוצות',
      value: stats?.totalGroups || 0,
      icon: 'bi-people-fill',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
  ]

  return (
    <div style={{ padding: 0, direction: 'rtl' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: 700,
          color: '#1a1a2e',
          margin: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          display: 'inline-block'
        }}>
          ניהול ספריות קבוצות
        </h1>
        <p style={{ color: '#666', marginTop: '8px', fontSize: '14px' }}>
          יצירה וניהול ספריות קבוצות עם חיפוש מתקדם וקטגוריות
        </p>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px',
        marginBottom: '32px',
      }}>
        {statsData.map((stat, i) => (
          <div
            key={i}
            onMouseEnter={() => setHoveredCard(i)}
            onMouseLeave={() => setHoveredCard(null)}
            style={{
              position: 'relative',
              background: hoveredCard === i
                ? '#ddd'
                : '#f8f9fa',
              borderRadius: '20px',
              padding: '24px',
              transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              transform: hoveredCard === i ? 'translateY(-8px) scale(1.02)' : 'none',
              boxShadow: hoveredCard === i
                ? '0 20px 40px rgba(0,0,0,0.4), 0 0 0 1px #ccc'
                : '0 4px 20px rgba(0,0,0,0.2)',
              border: '1px solid #ddd',
              overflow: 'hidden',
            }}
          >
            <div style={{
              position: 'absolute',
              top: '-50%',
              right: '-50%',
              width: '100%',
              height: '100%',
              background: stat.gradient,
              opacity: hoveredCard === i ? 0.15 : 0.05,
              borderRadius: '50%',
              filter: 'blur(60px)',
              transition: 'opacity 0.4s ease',
              pointerEvents: 'none',
            }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '16px',
                background: stat.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
              }}>
                <i className={`bi ${stat.icon}`} style={{ fontSize: '24px', color: '#fff' }}></i>
              </div>
              <div style={{ fontSize: '36px', fontWeight: 800, color: '#1a1a2e', lineHeight: 1 }}>
                {stat.value.toLocaleString()}
              </div>
              <div style={{ fontSize: '14px', color: '#555', marginTop: '8px', fontWeight: 500 }}>
                {stat.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        background: '#fafafa',
        padding: '8px',
        borderRadius: '12px',
        width: 'fit-content'
      }}>
        {[
          { id: 'dashboard', label: 'דאשבורד', icon: 'bi-graph-up' },
          { id: 'public-libraries', label: 'ספריות ציבוריות', icon: 'bi-globe' },
          { id: 'private-libraries', label: 'ספריות פרטיות', icon: 'bi-lock' },
          { id: 'groups', label: 'חיפוש קבוצות', icon: 'bi-search' },
          { id: 'categories', label: 'קטגוריות', icon: 'bi-tags' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id as any)
              setCurrentPage(1)
            }}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: activeTab === tab.id
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'transparent',
              color: activeTab === tab.id ? '#fff' : '#1a1a2e',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: activeTab === tab.id ? 600 : 400,
              transition: 'all 0.2s ease'
            }}
          >
            <i className={`bi ${tab.icon}`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div style={{
          background: 'linear-gradient(180deg, #f5f5f5 0%, #fafafa 100%)',
          borderRadius: '20px',
          border: '1px solid #ddd',
          padding: '28px',
        }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '3px solid #e0e0e0',
                borderTopColor: '#667eea',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 20px'
              }}></div>
              <div style={{ color: '#666' }}>טוען דאשבורד...</div>
            </div>
          ) : (
            <>
              <h3 style={{ color: '#1a1a2e', marginTop: 0, marginBottom: '24px', fontSize: '18px' }}>
                כיסוי ספריות
              </h3>

              {/* Coverage Progress */}
              <div style={{
                background: '#f8f9fa',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ color: '#555', fontSize: '14px' }}>קמפיינים עם ספרייה מוקצית</span>
                  <span style={{ color: '#1a1a2e', fontWeight: 600 }}>
                    {coverageStats?.campaigns.libraryAssignmentPercent || 0}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '10px',
                  background: '#ccc',
                  borderRadius: '5px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${coverageStats?.campaigns.libraryAssignmentPercent || 0}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '5px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
                <div style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
                  {coverageStats?.campaigns.withLibrary || 0} מתוך {coverageStats?.campaigns.total || 0} קמפיינים פעילים
                </div>
              </div>

              {/* Dashboard Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div style={{
                  background: 'rgba(102, 126, 234, 0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#667eea' }}>
                    {coverageStats?.libraries.public || 0}
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>ספריות ציבוריות</div>
                </div>
                <div style={{
                  background: 'rgba(17, 153, 142, 0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#11998e' }}>
                    {coverageStats?.libraries.finished || 0}
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>ספריות מוכנות</div>
                </div>
                <div style={{
                  background: 'rgba(240, 147, 251, 0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#f093fb' }}>
                    {coverageStats?.accounts.active || 0}
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>חשבונות פעילים</div>
                </div>
              </div>

              {/* Quick Actions */}
              <div style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => navigate('/admin/campaign-categorization')}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <i className="bi bi-tags"></i>
                  סיווג קמפיינים
                </button>
                <button
                  onClick={() => setActiveTab('public-libraries')}
                  style={{
                    padding: '12px 20px',
                    borderRadius: '10px',
                    border: '1px solid #bbb',
                    background: 'transparent',
                    color: '#1a1a2e',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <i className="bi bi-collection"></i>
                  נהל ספריות
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Libraries Tab */}
      {(activeTab === 'public-libraries' || activeTab === 'private-libraries') && (
        <>
          {/* Search & Actions */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '24px',
            alignItems: 'center',
            background: '#fafafa',
            padding: '16px 20px',
            borderRadius: '16px',
            border: '1px solid #eee',
          }}>
            <form onSubmit={handleSearch} style={{ flex: 1, position: 'relative' }}>
              <i className="bi bi-search" style={{
                position: 'absolute',
                right: '18px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#666',
                fontSize: '18px',
              }}></i>
              <input
                type="text"
                placeholder="חיפוש ספריות..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 52px 14px 20px',
                  background: '#f5f5f5',
                  border: '2px solid #ddd',
                  borderRadius: '12px',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </form>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1) }}
              style={{
                padding: '14px 16px',
                borderRadius: '12px',
                border: '2px solid #ddd',
                background: '#f5f5f5',
                color: '#1a1a2e',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer',
                minWidth: '130px'
              }}
            >
              <option value="">כל הסטטוסים</option>
              <option value="draft">טיוטה</option>
              <option value="in_progress">בעבודה</option>
              <option value="needs_review">לבדיקה</option>
              <option value="finished">מוכן</option>
              <option value="archived">ארכיון</option>
            </select>

            {industries.length > 0 && (
              <select
                value={industryFilter}
                onChange={(e) => { setIndustryFilter(e.target.value); setCurrentPage(1) }}
                style={{
                  padding: '14px 16px',
                  borderRadius: '12px',
                  border: '2px solid #ddd',
                  background: '#f5f5f5',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer',
                  minWidth: '130px'
                }}
              >
                <option value="">כל התעשיות</option>
                {industries.map(ind => (
                  <option key={ind._id} value={ind._id}>{ind.name_he}</option>
                ))}
              </select>
            )}

            {/* Modified (updated_at) date-range filter */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px', borderRadius: '12px', border: '2px solid #ddd', background: '#f5f5f5',
            }}>
              <i className="bi bi-pencil-square" style={{ color: '#888', fontSize: '13px' }} title="עודכן בטווח"></i>
              <span style={{ fontSize: '12px', color: '#888' }}>עודכן:</span>
              <input
                type="date"
                value={modifiedFrom}
                max={modifiedTo || undefined}
                onChange={(e) => { setModifiedFrom(e.target.value); setCurrentPage(1) }}
                style={{ border: 'none', background: 'transparent', color: '#1a1a2e', fontSize: '13px', outline: 'none', cursor: 'pointer' }}
              />
              <span style={{ color: '#bbb' }}>–</span>
              <input
                type="date"
                value={modifiedTo}
                min={modifiedFrom || undefined}
                onChange={(e) => { setModifiedTo(e.target.value); setCurrentPage(1) }}
                style={{ border: 'none', background: 'transparent', color: '#1a1a2e', fontSize: '13px', outline: 'none', cursor: 'pointer' }}
              />
              {(modifiedFrom || modifiedTo) && (
                <button
                  onClick={() => { setModifiedFrom(''); setModifiedTo(''); setCurrentPage(1) }}
                  title="נקה טווח תאריכים"
                  style={{ border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: '14px', padding: 0, lineHeight: 1 }}
                >
                  <i className="bi bi-x-circle"></i>
                </button>
              )}
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: '14px 24px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              }}
            >
              <i className="bi bi-plus-lg"></i>
              ספרייה חדשה
            </button>
          </div>

          {/* Libraries Table */}
          <div style={{
            background: 'linear-gradient(180deg, #f5f5f5 0%, #fafafa 100%)',
            borderRadius: '20px',
            border: '1px solid #ddd',
            overflow: 'hidden',
          }}>
            {/* Bulk Action Bar */}
            {selectedLibraries.size > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '16px 28px',
                background: 'rgba(102, 126, 234, 0.1)',
                borderBottom: '1px solid rgba(102, 126, 234, 0.2)',
              }}>
                <span style={{ color: '#1a1a2e', fontWeight: 600, fontSize: '14px' }}>
                  {selectedLibraries.size} ספריות נבחרו
                </span>
                {activeTab === 'private-libraries' && (
                  <button
                    onClick={handleBulkMakePublic}
                    disabled={bulkUpdating}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      color: '#fff',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: bulkUpdating ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      opacity: bulkUpdating ? 0.7 : 1,
                    }}
                  >
                    <i className="bi bi-globe"></i>
                    העבר לציבורי
                  </button>
                )}
                {activeTab === 'public-libraries' && (
                  <button
                    onClick={handleBulkMakePrivate}
                    disabled={bulkUpdating}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(108, 114, 147, 0.3)',
                      color: '#1a1a2e',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: bulkUpdating ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      opacity: bulkUpdating ? 0.7 : 1,
                    }}
                  >
                    <i className="bi bi-lock"></i>
                    העבר לפרטי
                  </button>
                )}
                <button
                  onClick={() => setShowBulkAssignAccountModal(true)}
                  disabled={bulkUpdating}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #3699ff 0%, #1b84ff 100%)',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: bulkUpdating ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: bulkUpdating ? 0.7 : 1,
                  }}
                >
                  <i className="bi bi-person-fill-gear"></i>
                  שייך לחשבון
                </button>
                <button
                  onClick={() => setShowBulkAssignOccupationModal(true)}
                  disabled={bulkUpdating}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: bulkUpdating ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: bulkUpdating ? 0.7 : 1,
                  }}
                >
                  <i className="bi bi-briefcase-fill"></i>
                  שייך עיסוק
                </button>
                <button
                  onClick={() => setSelectedLibraries(new Set())}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #bbb',
                    background: 'transparent',
                    color: '#1a1a2e',
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  נקה בחירה
                </button>
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: '40px 2fr 1fr 1fr 1fr 1fr 1fr 160px',
              padding: '20px 28px',
              background: '#f8f9fa',
              borderBottom: '1px solid #ddd',
            }}>
              <div
                onClick={selectAllLibraries}
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '6px',
                  border: `2px solid ${selectedLibraries.size === libraries.length && libraries.length > 0 ? '#667eea' : '#bbb'}`,
                  background: selectedLibraries.size === libraries.length && libraries.length > 0 ? '#667eea' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {selectedLibraries.size === libraries.length && libraries.length > 0 && (
                  <i className="bi bi-check" style={{ color: '#fff', fontSize: '12px' }}></i>
                )}
              </div>
              {['שם הספרייה', 'חשבון', 'קבוצות', 'קטגוריות', 'נוצרה', 'עודכן', 'פעולות'].map((header, i) => (
                <div key={i} style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: '#666',
                }}>
                  {header}
                </div>
              ))}
            </div>

            {loading ? (
              <div style={{ padding: '80px', textAlign: 'center' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: '3px solid #e0e0e0',
                  borderTopColor: '#667eea',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  margin: '0 auto 20px'
                }}></div>
                <div style={{ color: '#666' }}>טוען ספריות...</div>
              </div>
            ) : libraries.length === 0 ? (
              <div style={{ padding: '80px', textAlign: 'center' }}>
                <i className="bi bi-collection" style={{ fontSize: '48px', color: '#666', display: 'block', marginBottom: '16px' }}></i>
                <div style={{ color: '#1a1a2e', fontSize: '16px', fontWeight: 600 }}>לא נמצאו ספריות</div>
                <div style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>צור ספרייה חדשה כדי להתחיל</div>
              </div>
            ) : (
              libraries.map((library) => (
                <div
                  key={library._id}
                  onClick={() => navigate(`/admin/libraries/${library._id}`)}
                  onMouseEnter={() => setHoveredRow(library._id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 2fr 1fr 1fr 1fr 1fr 1fr 160px',
                    padding: '18px 28px',
                    alignItems: 'center',
                    borderBottom: '1px solid #f5f5f5',
                    cursor: 'pointer',
                    background: selectedLibraries.has(library._id)
                      ? 'rgba(102, 126, 234, 0.1)'
                      : hoveredRow === library._id
                        ? 'linear-gradient(90deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)'
                        : 'transparent',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div
                    onClick={(e) => toggleLibrarySelection(library._id, e)}
                    style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '6px',
                      border: `2px solid ${selectedLibraries.has(library._id) ? '#667eea' : '#bbb'}`,
                      background: selectedLibraries.has(library._id) ? '#667eea' : 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {selectedLibraries.has(library._id) && (
                      <i className="bi bi-check" style={{ color: '#fff', fontSize: '12px' }}></i>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <i className="bi bi-collection" style={{ color: '#fff', fontSize: '18px' }}></i>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#1a1a2e', fontSize: '14px' }}>{library.name}</div>
                      {occupationLabel(library.occupation, library.occupation_other) && (
                        <span style={{
                          display: 'inline-block',
                          marginTop: '4px',
                          padding: '2px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: 'rgba(245, 87, 108, 0.1)',
                          color: '#f5576c',
                        }}>
                          <i className="bi bi-briefcase-fill" style={{ fontSize: '10px', marginLeft: '4px' }}></i>
                          {occupationLabel(library.occupation, library.occupation_other)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ fontSize: '13px', color: '#1a1a2e' }}>
                    {library.account ? (
                      <div>
                        <div style={{ fontWeight: 500 }}>{library.account.first_name} {library.account.last_name}</div>
                        {library.account.email && <div style={{ fontSize: '11px', color: '#888' }}>{library.account.email}</div>}
                      </div>
                    ) : (
                      <span style={{ color: '#999' }}>—</span>
                    )}
                  </div>

                  <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '15px' }}>
                    {library.group_count}
                  </div>

                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {library.categories && library.categories.length > 0 ? (
                      library.categories.map(cat => (
                        <span key={cat._id} style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 500,
                          background: 'rgba(240, 147, 251, 0.1)',
                          color: '#f093fb',
                        }}>
                          {cat.name}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: '#666', fontSize: '12px' }}>—</span>
                    )}
                  </div>

                  <div style={{ color: '#555', fontSize: '13px' }}>
                    {moment(library.created_at).format('DD/MM/YYYY')}
                  </div>

                  <div style={{ color: '#555', fontSize: '13px' }} title="עודכן לאחרונה">
                    {(library.modified_at || library.updated_at) ? (
                      <>
                        {moment(library.modified_at || library.updated_at).format('DD/MM/YYYY')}
                        <div style={{ color: '#999', fontSize: '11px', marginTop: '2px' }}>
                          {moment(library.modified_at || library.updated_at).format('HH:mm')}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: '#bbb' }}>—</span>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }} onClick={e => e.stopPropagation()}>
                    {activeTab === 'private-libraries' ? (
                      <button
                        onClick={() => handleMakePublic(library._id, library.name)}
                        title="העבר לציבורי"
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          border: 'none',
                          background: 'rgba(17, 153, 142, 0.1)',
                          color: '#11998e',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <i className="bi bi-globe"></i>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleMakePrivate(library._id, library.name)}
                        title="העבר לפרטי"
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          border: 'none',
                          background: 'rgba(108, 114, 147, 0.1)',
                          color: '#666',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <i className="bi bi-lock"></i>
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/admin/libraries/${library._id}`)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'rgba(102, 126, 234, 0.1)',
                        color: '#667eea',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteLibrary(library._id, library.name)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'rgba(255, 107, 107, 0.1)',
                        color: '#ff6b6b',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              ))
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 28px',
                gap: '8px',
              }}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    border: 'none',
                    background: currentPage === 1 ? '#f8f9fa' : '#eee',
                    color: currentPage === 1 ? '#aaa' : '#333',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <span style={{ color: '#555', margin: '0 16px' }}>
                  עמוד {currentPage} מתוך {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    border: 'none',
                    background: currentPage === totalPages ? '#f8f9fa' : '#eee',
                    color: currentPage === totalPages ? '#aaa' : '#333',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Groups Search Tab */}
      {activeTab === 'groups' && (
        <>
          {/* Search Controls */}
          <div style={{
            background: '#fafafa',
            padding: '24px',
            borderRadius: '16px',
            border: '1px solid #eee',
            marginBottom: '24px',
          }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#555', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                מילות חיפוש (מופרדות בפסיק או רווח - כולן חייבות להתאים)
              </label>
              <input
                type="text"
                placeholder="לדוגמה: נדל״ן, תל אביב, קניה"
                value={groupKeywords}
                onChange={(e) => setGroupKeywords(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: '#f5f5f5',
                  border: '2px solid #ddd',
                  borderRadius: '12px',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ color: '#555', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                  סנן לפי קטגוריה
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    borderRadius: '12px',
                    border: '2px solid #ddd',
                    background: '#f5f5f5',
                    color: '#1a1a2e',
                    fontSize: '14px',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">כל הקטגוריות</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat._id}>{cat.name} ({cat.group_count})</option>
                  ))}
                </select>
              </div>

              <div style={{ width: '150px' }}>
                <label style={{ color: '#555', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                  מינימום עוקבים
                </label>
                <input
                  type="number"
                  placeholder="0"
                  value={minFollowers}
                  onChange={(e) => setMinFollowers(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 20px',
                    background: '#f5f5f5',
                    border: '2px solid #ddd',
                    borderRadius: '12px',
                    color: '#1a1a2e',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                />
              </div>

              <button
                onClick={() => handleSearchGroups(1)}
                disabled={searchingGroups}
                style={{
                  padding: '14px 32px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: searchingGroups ? 'not-allowed' : 'pointer',
                  opacity: searchingGroups ? 0.7 : 1,
                }}
              >
                {searchingGroups ? 'מחפש...' : 'חפש קבוצות'}
              </button>
            </div>
          </div>

          {/* Selection Actions */}
          {selectedGroups.size > 0 && (
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '24px',
              padding: '16px 20px',
              background: 'rgba(102, 126, 234, 0.1)',
              borderRadius: '12px',
              alignItems: 'center',
            }}>
              <span style={{ color: '#1a1a2e', fontWeight: 600 }}>
                {selectedGroups.size} קבוצות נבחרו
              </span>
              <button
                onClick={() => setShowBulkCategoryModal(true)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: '#fff',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <i className="bi bi-tags me-2"></i>
                החל קטגוריות
              </button>
              <button
                onClick={clearSelection}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #bbb',
                  background: 'transparent',
                  color: '#1a1a2e',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                נקה בחירה
              </button>
            </div>
          )}

          {/* Groups Results */}
          {searchedGroups.length > 0 && (
            <div style={{
              background: 'linear-gradient(180deg, #f5f5f5 0%, #fafafa 100%)',
              borderRadius: '20px',
              border: '1px solid #ddd',
              overflow: 'hidden',
              position: 'relative',
            }}>
              {/* Loading Overlay */}
              {searchingGroups && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '20px',
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                  }}>
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span style={{ color: '#667eea', fontWeight: 500 }}>טוען קבוצות...</span>
                  </div>
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 28px',
                background: '#f8f9fa',
                borderBottom: '1px solid #ddd',
              }}>
                <span style={{ color: '#555', fontSize: '13px' }}>
                  נמצאו {searchedGroups.length} קבוצות
                </span>
                <button
                  onClick={selectAllGroups}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #bbb',
                    background: 'transparent',
                    color: '#1a1a2e',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  בחר הכל
                </button>
              </div>

              {searchedGroups.map((group) => (
                <div
                  key={group._id}
                  onClick={() => toggleGroupSelection(group._id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 2fr 1fr 1fr',
                    padding: '16px 28px',
                    alignItems: 'center',
                    borderBottom: '1px solid #f5f5f5',
                    cursor: 'pointer',
                    background: selectedGroups.has(group._id)
                      ? 'rgba(102, 126, 234, 0.1)'
                      : 'transparent',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '6px',
                    border: `2px solid ${selectedGroups.has(group._id) ? '#667eea' : '#bbb'}`,
                    background: selectedGroups.has(group._id) ? '#667eea' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {selectedGroups.has(group._id) && (
                      <i className="bi bi-check" style={{ color: '#fff', fontSize: '14px' }}></i>
                    )}
                  </div>

                  <div>
                    <div style={{ fontWeight: 600, color: '#1a1a2e', fontSize: '14px' }}>{group.name}</div>
                    {group.location && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                        <i className="bi bi-geo-alt me-1"></i>
                        {group.location}
                      </div>
                    )}
                  </div>

                  <div style={{ color: '#555', fontSize: '13px' }}>
                    {group.follow_count?.toLocaleString() || 0} עוקבים
                  </div>

                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {Array.isArray(group.category) && group.category.map(cat => (
                      <span key={cat._id} style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        background: 'rgba(240, 147, 251, 0.1)',
                        color: '#f093fb',
                      }}>
                        {cat.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {groupSearchTotalPages > 1 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px 28px',
                  gap: '8px',
                }}>
                  <button
                    onClick={() => handleSearchGroups(groupSearchPage - 1)}
                    disabled={groupSearchPage === 1 || searchingGroups}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '12px',
                      border: 'none',
                      background: groupSearchPage === 1 || searchingGroups ? '#f8f9fa' : '#eee',
                      color: groupSearchPage === 1 || searchingGroups ? '#aaa' : '#333',
                      cursor: groupSearchPage === 1 || searchingGroups ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <span style={{ color: '#555', margin: '0 16px' }}>
                    עמוד {groupSearchPage} מתוך {groupSearchTotalPages}
                  </span>
                  <button
                    onClick={() => handleSearchGroups(groupSearchPage + 1)}
                    disabled={groupSearchPage === groupSearchTotalPages || searchingGroups}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '12px',
                      border: 'none',
                      background: groupSearchPage === groupSearchTotalPages || searchingGroups ? '#f8f9fa' : '#eee',
                      color: groupSearchPage === groupSearchTotalPages || searchingGroups ? '#aaa' : '#333',
                      cursor: groupSearchPage === groupSearchTotalPages || searchingGroups ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '24px',
          }}>
            <button
              onClick={() => setShowCategoryModal(true)}
              style={{
                padding: '14px 24px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <i className="bi bi-plus-lg"></i>
              קטגוריה חדשה
            </button>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px',
          }}>
            {categories.map((category) => (
              <div
                key={category._id}
                style={{
                  background: '#f8f9fa',
                  borderRadius: '16px',
                  padding: '20px',
                  border: '1px solid #ddd',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <i className="bi bi-tag" style={{ color: '#fff', fontSize: '18px' }}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1a1a2e', fontSize: '15px' }}>{category.name}</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>{category.group_count} קבוצות</div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteCategory(category._id, category.name)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'rgba(255, 107, 107, 0.1)',
                    color: '#ff6b6b',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create Library Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div
            style={{
              background: '#fff',
              borderRadius: '20px',
              padding: '32px',
              width: '100%',
              maxWidth: '480px',
              border: '1px solid #ccc',
            }}
          >
            <h3 style={{ color: '#1a1a2e', marginTop: 0, marginBottom: '24px' }}>יצירת ספרייה חדשה</h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#555', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                שם הספרייה
              </label>
              <input
                type="text"
                value={newLibraryName}
                onChange={(e) => setNewLibraryName(e.target.value)}
                placeholder="הזן שם לספרייה"
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: '#f5f5f5',
                  border: '2px solid #ddd',
                  borderRadius: '12px',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#555', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                קטגוריות
              </label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                padding: '12px',
                background: '#fafafa',
                borderRadius: '12px',
                border: '2px solid #ddd',
              }}>
                {categories.map(cat => (
                  <button
                    key={cat._id}
                    type="button"
                    onClick={() => {
                      setNewLibraryCategories(prev =>
                        prev.includes(cat._id)
                          ? prev.filter(id => id !== cat._id)
                          : [...prev, cat._id]
                      )
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      background: newLibraryCategories.includes(cat._id)
                        ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                        : '#eee',
                      color: newLibraryCategories.includes(cat._id) ? '#fff' : '#1a1a2e',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
                {categories.length === 0 && (
                  <span style={{ color: '#666', fontSize: '13px' }}>אין קטגוריות - צור קטגוריות בלשונית "קטגוריות"</span>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#555', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                שייך לחשבון (אופציונלי)
              </label>
              {newLibraryAccountId ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  background: '#e8f4fd',
                  borderRadius: '10px',
                  border: '2px solid #3699ff',
                }}>
                  <i className="bi bi-person-fill" style={{ color: '#3699ff' }}></i>
                  <span style={{ flex: 1, color: '#1a1a2e', fontSize: '14px' }}>{newLibraryAccountName}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setNewLibraryAccountId('')
                      setNewLibraryAccountName('')
                      setAccountSearchQuery('')
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#999',
                      cursor: 'pointer',
                      fontSize: '16px',
                    }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={accountSearchQuery}
                    onChange={async (e) => {
                      const q = e.target.value
                      setAccountSearchQuery(q)
                      if (q.trim().length < 2) {
                        setAccountSearchResults([])
                        return
                      }
                      setSearchingAccounts(true)
                      try {
                        const res = await searchAccounts(q.trim())
                        setAccountSearchResults(res.accounts || [])
                      } catch {
                        setAccountSearchResults([])
                      } finally {
                        setSearchingAccounts(false)
                      }
                    }}
                    placeholder="חפש לפי שם, אימייל או טלפון..."
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      background: '#f5f5f5',
                      border: '2px solid #ddd',
                      borderRadius: '12px',
                      color: '#1a1a2e',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                  />
                  {accountSearchQuery.trim().length >= 2 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      marginTop: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 10,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}>
                      {searchingAccounts ? (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#888', fontSize: '13px' }}>מחפש...</div>
                      ) : accountSearchResults.length === 0 ? (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#888', fontSize: '13px' }}>לא נמצאו תוצאות</div>
                      ) : (
                        accountSearchResults.map((acc: any) => (
                          <div
                            key={acc._id}
                            onClick={() => {
                              setNewLibraryAccountId(acc._id)
                              setNewLibraryAccountName(`${acc.first_name || ''} ${acc.last_name || ''} (${acc.email || acc.phone || ''})`.trim())
                              setAccountSearchQuery('')
                              setAccountSearchResults([])
                            }}
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '13px',
                              color: '#1a1a2e',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5ff')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                          >
                            <div style={{ fontWeight: 500 }}>{acc.first_name} {acc.last_name}</div>
                            <div style={{ color: '#888', fontSize: '12px' }}>{acc.email || acc.phone}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: '1px solid #bbb',
                  background: 'transparent',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                ביטול
              </button>
              <button
                onClick={handleCreateLibrary}
                disabled={creating || !newLibraryName.trim()}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: creating ? 'not-allowed' : 'pointer',
                  opacity: creating || !newLibraryName.trim() ? 0.7 : 1,
                }}
              >
                {creating ? 'יוצר...' : 'צור ספרייה'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Account Modal */}
      {showBulkAssignAccountModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '32px',
            width: '100%',
            maxWidth: '480px',
            border: '1px solid #ccc',
          }}>
            <h3 style={{ color: '#1a1a2e', marginTop: 0, marginBottom: '8px' }}>שייך ספריות לחשבון</h3>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
              {selectedLibraries.size} ספריות נבחרו
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#555', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                בחר חשבון
              </label>
              {bulkAssignAccountId ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  background: '#e8f4fd',
                  borderRadius: '10px',
                  border: '2px solid #3699ff',
                }}>
                  <i className="bi bi-person-fill" style={{ color: '#3699ff' }}></i>
                  <span style={{ flex: 1, color: '#1a1a2e', fontSize: '14px' }}>{bulkAssignAccountName}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setBulkAssignAccountId('')
                      setBulkAssignAccountName('')
                      setBulkAssignAccountSearch('')
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#999',
                      cursor: 'pointer',
                      fontSize: '16px',
                    }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={bulkAssignAccountSearch}
                    onChange={async (e) => {
                      const q = e.target.value
                      setBulkAssignAccountSearch(q)
                      if (q.trim().length < 2) {
                        setBulkAssignAccountResults([])
                        return
                      }
                      setBulkAssignSearching(true)
                      try {
                        const res = await searchAccounts(q.trim())
                        setBulkAssignAccountResults(res.accounts || [])
                      } catch {
                        setBulkAssignAccountResults([])
                      } finally {
                        setBulkAssignSearching(false)
                      }
                    }}
                    placeholder="חפש לפי שם, אימייל או טלפון..."
                    style={{
                      width: '100%',
                      padding: '14px 20px',
                      background: '#f5f5f5',
                      border: '2px solid #ddd',
                      borderRadius: '12px',
                      color: '#1a1a2e',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                  />
                  {bulkAssignAccountSearch.trim().length >= 2 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      marginTop: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 10,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}>
                      {bulkAssignSearching ? (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#888', fontSize: '13px' }}>מחפש...</div>
                      ) : bulkAssignAccountResults.length === 0 ? (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#888', fontSize: '13px' }}>לא נמצאו תוצאות</div>
                      ) : (
                        bulkAssignAccountResults.map((acc: any) => (
                          <div
                            key={acc._id}
                            onClick={() => {
                              setBulkAssignAccountId(acc._id)
                              setBulkAssignAccountName(`${acc.first_name || ''} ${acc.last_name || ''} (${acc.email || acc.phone || ''})`.trim())
                              setBulkAssignAccountSearch('')
                              setBulkAssignAccountResults([])
                            }}
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '13px',
                              color: '#1a1a2e',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5ff')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                          >
                            <div style={{ fontWeight: 500 }}>{acc.first_name} {acc.last_name}</div>
                            <div style={{ color: '#888', fontSize: '12px' }}>{acc.email || acc.phone}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowBulkAssignAccountModal(false)
                  setBulkAssignAccountId('')
                  setBulkAssignAccountName('')
                  setBulkAssignAccountSearch('')
                  setBulkAssignAccountResults([])
                }}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: '1px solid #bbb',
                  background: 'transparent',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                ביטול
              </button>
              <button
                onClick={handleBulkAssignAccount}
                disabled={bulkUpdating || !bulkAssignAccountId}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #3699ff 0%, #1b84ff 100%)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: bulkUpdating || !bulkAssignAccountId ? 'not-allowed' : 'pointer',
                  opacity: bulkUpdating || !bulkAssignAccountId ? 0.7 : 1,
                }}
              >
                {bulkUpdating ? 'משייך...' : 'שייך לחשבון'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Occupation Modal */}
      {showBulkAssignOccupationModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '32px',
            width: '100%',
            maxWidth: '480px',
            border: '1px solid #ccc',
          }}>
            <h3 style={{ color: '#1a1a2e', marginTop: 0, marginBottom: '8px' }}>שייך עיסוק לספריות</h3>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
              {selectedLibraries.size} ספריות נבחרו
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#555', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                בחר עיסוק
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {OCCUPATION_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setBulkOccupation(opt.value)}
                    style={{
                      padding: '10px 18px',
                      borderRadius: '10px',
                      border: `2px solid ${bulkOccupation === opt.value ? '#f5576c' : '#ddd'}`,
                      background: bulkOccupation === opt.value ? 'rgba(245, 87, 108, 0.1)' : '#f5f5f5',
                      color: bulkOccupation === opt.value ? '#f5576c' : '#1a1a2e',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {bulkOccupation === 'other' && (
                <input
                  type="text"
                  value={bulkOccupationOther}
                  onChange={(e) => setBulkOccupationOther(e.target.value)}
                  placeholder="הזן תיאור עיסוק..."
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    padding: '14px 20px',
                    background: '#f5f5f5',
                    border: '2px solid #ddd',
                    borderRadius: '12px',
                    color: '#1a1a2e',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                />
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowBulkAssignOccupationModal(false)
                  setBulkOccupation('')
                  setBulkOccupationOther('')
                }}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: '1px solid #bbb',
                  background: 'transparent',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                ביטול
              </button>
              <button
                onClick={handleBulkAssignOccupation}
                disabled={bulkUpdating || !bulkOccupation || (bulkOccupation === 'other' && !bulkOccupationOther.trim())}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: bulkUpdating || !bulkOccupation || (bulkOccupation === 'other' && !bulkOccupationOther.trim()) ? 'not-allowed' : 'pointer',
                  opacity: bulkUpdating || !bulkOccupation || (bulkOccupation === 'other' && !bulkOccupationOther.trim()) ? 0.7 : 1,
                }}
              >
                {bulkUpdating ? 'משייך...' : 'שייך עיסוק'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Category Modal */}
      {showCategoryModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div
            style={{
              background: '#fff',
              borderRadius: '20px',
              padding: '32px',
              width: '100%',
              maxWidth: '400px',
              border: '1px solid #ccc',
            }}
          >
            <h3 style={{ color: '#1a1a2e', marginTop: 0, marginBottom: '24px' }}>יצירת קטגוריה חדשה</h3>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ color: '#555', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                שם הקטגוריה
              </label>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="הזן שם לקטגוריה"
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: '#f5f5f5',
                  border: '2px solid #ddd',
                  borderRadius: '12px',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCategoryModal(false)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: '1px solid #bbb',
                  background: 'transparent',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                ביטול
              </button>
              <button
                onClick={handleCreateCategory}
                disabled={!newCategoryName.trim()}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: !newCategoryName.trim() ? 'not-allowed' : 'pointer',
                  opacity: !newCategoryName.trim() ? 0.7 : 1,
                }}
              >
                צור קטגוריה
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Category Modal */}
      {showBulkCategoryModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div
            style={{
              background: '#fff',
              borderRadius: '20px',
              padding: '32px',
              width: '100%',
              maxWidth: '480px',
              border: '1px solid #ccc',
            }}
          >
            <h3 style={{ color: '#1a1a2e', marginTop: 0, marginBottom: '24px' }}>
              החל קטגוריות ל-{selectedGroups.size} קבוצות
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#555', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                פעולה
              </label>
              <select
                value={bulkMode}
                onChange={(e) => setBulkMode(e.target.value as any)}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  borderRadius: '12px',
                  border: '2px solid #ddd',
                  background: '#f5f5f5',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value="add">הוסף קטגוריות</option>
                <option value="remove">הסר קטגוריות</option>
                <option value="set">החלף קטגוריות</option>
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ color: '#555', fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                בחר קטגוריות
              </label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '12px',
                background: '#fafafa',
                borderRadius: '12px',
              }}>
                {categories.map(cat => (
                  <button
                    key={cat._id}
                    onClick={() => {
                      setBulkCategoryIds(prev =>
                        prev.includes(cat._id)
                          ? prev.filter(id => id !== cat._id)
                          : [...prev, cat._id]
                      )
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      background: bulkCategoryIds.includes(cat._id)
                        ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                        : '#eee',
                      color: bulkCategoryIds.includes(cat._id) ? '#fff' : '#1a1a2e',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowBulkCategoryModal(false)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: '1px solid #bbb',
                  background: 'transparent',
                  color: '#1a1a2e',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                ביטול
              </button>
              <button
                onClick={handleBulkCategoryApply}
                disabled={bulkCategoryIds.length === 0}
                style={{
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: bulkCategoryIds.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: bulkCategoryIds.length === 0 ? 0.7 : 1,
                }}
              >
                החל
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        select option {
          background: #fff;
          color: #333;
        }
      `}</style>
    </div>
  )
}

export default LibrariesPage
